import { useState } from "react";
import { DashboardLayout } from "../../client/components/Sidebar";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  MapPin,
  Building2,
  Users,
  Calendar,
  Megaphone,
  Clock,
  Globe,
  Phone,
  Navigation,
  ShoppingBag,
  Utensils,
  Wrench,
  Briefcase,
  ShoppingCart,
  HandHeart,
  Home,
  Search,
  Car,
  Gift,
  MessageCircleQuestion,
  AlertCircle,
  HelpCircle,
  GraduationCap,
  BookOpen,
  Bell,
  Loader2,
  Star,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { pageLayoutStyles } from "../../components/ui/page-layout";
import { cn } from "../../lib/utils";
import type { Location, LocationMembership } from "wasp/entities";
import { useViewMode } from "../../client/contexts/ViewModeContext";
import { useAction, useQuery } from "wasp/client/operations";
import { subscribeToLocation, getUserMembership, updateLocationSubscription, updateUserPrimaryLocation, getMyProfile } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import toast from "react-hot-toast";

interface LocationReceptionPageProps {
  location: Location;
}

export default function LocationReceptionPage({
  location,
}: LocationReceptionPageProps) {
  const navigate = useNavigate();
  const { isHome, isStudentLife, isAcademics, isCampus } = useViewMode();
  const { data: user } = useAuth();
  const { data: profile, refetch: refetchProfile } = useQuery(getMyProfile, { enabled: !!user });
  const { data: membership, refetch: refetchMembership } = useQuery(getUserMembership, { locationId: location.id }, { enabled: !!user });
  const subscribeAction = useAction(subscribeToLocation);
  const updateSubscriptionAction = useAction(updateLocationSubscription);
  const setPrimaryLocationAction = useAction(updateUserPrimaryLocation);

  const locMembership = membership as any as LocationMembership | null;
  const isSubscribed = locMembership?.isSubscribed ?? false;
  const isPrimary = (profile?.primaryLocationId || user?.primaryLocationId) === location.id;

  const [isPrimaryLoading, setIsPrimaryLoading] = useState(false);
  const [isSubsLoading, setIsSubsLoading] = useState(false);

  const handleSetPrimary = async () => {
    setIsPrimaryLoading(true);
    try {
      await setPrimaryLocationAction({
        userId: user?.id!,
        primaryLocationId: isPrimary ? null : location.id
      });
      if (isPrimary) {
        toast.success(`Removed ${location.name} as primary location`);
      } else {
        toast.success(`${location.name} set as primary location!`);
      }
      await refetchProfile();
    } catch (error: any) {
      toast.error(error.message || "Failed to update primary location");
    } finally {
      setIsPrimaryLoading(false);
    }
  };

  const handleToggleSubscription = async () => {
    setIsSubsLoading(true);
    try {
      if (!membership) {
        await subscribeAction({ locationId: location.id });
        toast.success(`Subscribed to ${location.name}!`);
      } else {
        await updateSubscriptionAction({
          userId: user?.id!,
          locationId: location.id,
          isSubscribed: !isSubscribed
        });
        toast.success(isSubscribed ? `Unsubscribed from ${location.name}` : `Subscribed to ${location.name}!`);
      }
      await refetchMembership();
    } catch (error: any) {
      toast.error(error.message || "Failed to update subscription");
    } finally {
      setIsSubsLoading(false);
    }
  };

  const getLocationTypeLabel = (type: string) => {
    return type
      ?.replace(/_/g, " ")
      .toLowerCase()
      ?.replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getLocationTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      APARTMENT_BUILDING: "text-blue-600",
      INNOVATION_HUB: "text-green-600",
      COWORKING_SPACE: "text-purple-600",
      SHOPPING_CENTER: "text-orange-600",
      CAMPUS: "text-indigo-600",
      COMMUNITY_CENTER: "text-pink-600",
      MIXED_USE: "text-gray-600",
      OTHER: "text-slate-600",
    };
    return colors[type] || colors.OTHER;
  };

  const formatAddress = () => {
    const parts = [
      location.address,
      location.city,
      location.state,
      location.country,
      location.postalCode,
    ];
    return parts.filter(Boolean).join(", ");
  };

  return (
    <DashboardLayout>
      <div className={pageLayoutStyles.layout.container}>
        {/* Location Header */}
        <div className="mb-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-4xl font-bold tracking-tight text-foreground">
                  {location.name}
                </h1>
              </div>
              {location.publicDescription && (
                <p className="text-lg text-muted-foreground max-w-3xl">
                  {location.publicDescription}
                </p>
              )}
            </div>
            {user && (
              <div className="flex gap-2">
                <Button
                  variant={isPrimary ? "default" : "outline"}
                  onClick={handleSetPrimary}
                  disabled={isPrimaryLoading}
                  className={cn(
                    "gap-2 transition-all",
                    isPrimary && "bg-amber-400 hover:bg-amber-500 border-amber-600 text-amber-950 shadow-sm"
                  )}
                >
                  {isPrimaryLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Star className={cn("w-4 h-4", isPrimary && "fill-amber-950 text-amber-950")} />
                  )}
                  {isPrimary ? "Primary Location" : "Set as Primary"}
                </Button>
                <Button
                  variant={isSubscribed ? "default" : "outline"}
                  onClick={handleToggleSubscription}
                  disabled={isSubsLoading}
                  className={cn(
                    "gap-2 transition-all",
                    isSubscribed && "bg-blue-600 hover:bg-blue-700 border-blue-700 text-white"
                  )}
                >
                  {isSubsLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Bell className={cn("w-4 h-4", isSubscribed && "fill-white text-white")} />
                  )}
                  {isSubscribed ? "Unsubscribe" : "Subscribe"}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Navigation className="w-5 h-5" />
                  Quick Actions
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {(isHome) && (
                    <>
                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/notices")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <Megaphone className="w-5 h-5 text-purple-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Notices</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Community announcements
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/events")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <Calendar className="w-5 h-5 text-blue-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Events</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Upcoming activities
                            </div>
                          </div>
                        </div>
                      </Button>
                    </>
                  )}

                  {isCampus && (
                    <>
                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/tenants")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <Building2 className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Tenants</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Community members
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/retail")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <ShoppingBag className="w-5 h-5 text-pink-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Retail</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Shops & stores
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/food-court")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <Utensils className="w-5 h-5 text-orange-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Food Court</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Dining & restaurants
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/facilities")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <MapPin className="w-5 h-5 text-teal-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Facilities</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Amenities & services
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/services")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <Wrench className="w-5 h-5 text-purple-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Services</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Professional services
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/jobs")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <Briefcase className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Jobs</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Work opportunities
                            </div>
                          </div>
                        </div>
                      </Button>
                    </>
                  )}

                  {isStudentLife && (
                    <>
                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/buy-sell")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <ShoppingCart className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Buy & Sell</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Marketplace items
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/request-offer")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <HandHeart className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Request & Offer</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Help & collaboration
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/accommodation")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <Home className="w-5 h-5 text-cyan-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Accommodation</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Housing & rentals
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/lost-found")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <Search className="w-5 h-5 text-blue-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Lost & Found</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Recover missing items
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/carpooling")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <Car className="w-5 h-5 text-blue-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Car Pooling</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Shared rides
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/share-with-neighbors")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <Gift className="w-5 h-5 text-green-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Share with Neighbors</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Borrow & borrow
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/other-requests")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <MessageCircleQuestion className="w-5 h-5 text-amber-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Other Requests</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Miscellaneous needs
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/immediate-help")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Time Sensitive Help</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Urgent assistance
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/ask-for-help")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <HelpCircle className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Ask for Help</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              General support
                            </div>
                          </div>
                        </div>
                      </Button>
                    </>
                  )}

                  {isAcademics && (
                    <>
                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/services?category=TUTORING")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <GraduationCap className="w-5 h-5 text-purple-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Tutor Help</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Academic assistance
                            </div>
                          </div>
                        </div>
                      </Button>

                      <Button
                        variant="outline"
                        className="h-auto p-4 justify-start whitespace-normal text-left min-w-0"
                        onClick={() => navigate("/textbooks")}
                      >
                        <div className="flex items-center gap-3 w-full min-w-0">
                          <BookOpen className="w-5 h-5 text-blue-600 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold truncate">Textbooks</div>
                            <div className="text-sm text-muted-foreground line-clamp-2">
                              Buy & sell books
                            </div>
                          </div>
                        </div>
                      </Button>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Recent Activity Placeholder */}
            <Card>
              <CardHeader>
                <CardTitle>Recent Activity</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Recent activity will appear here</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Location Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Location Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <Building2 className={cn("w-4 h-4 mt-0.5 flex-shrink-0", getLocationTypeColor(location.type))} />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      Type
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {getLocationTypeLabel(location.type)}
                    </p>
                  </div>
                </div>
                {formatAddress() && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        Address
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {formatAddress()}
                      </p>
                    </div>
                  </div>
                )}
                {location.officialWebsiteUrl && (
                  <div className="flex items-start gap-3 pt-2">
                    <Globe className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">
                        Official Website
                      </p>
                      <a 
                        href={location.officialWebsiteUrl} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600 hover:underline break-all block"
                      >
                        {location.officialWebsiteUrl}
                      </a>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Contact Information Placeholder */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Phone className="w-5 h-5" />
                  Contact
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-center py-4 text-muted-foreground">
                  <Phone className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Contact information not available</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
